import React, { useEffect, useMemo, useState } from 'react';
import { Chatbox, ConversationList } from '@talkjs/react-components';
import '@talkjs/react-components/default.css';
import { Box } from '@mui/material';
import '../Components/chat.css';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import SDCustomListHeader from '../Components/SDCustomListHeader';
import CustomConversationListItem from '../Components/CustomConversationListItem';
import SDCustomChatHeader from '../Components/SDCustomChatHeader';
import CustomMessage from '../Components/CustomMessage';
import { currentlyLoggedInOrImpersonatingUserHasAnyRoleInCurrentlySelectedSite } from '../../../services/auth';
import { Roles } from '../../../models/roles';
import routes from '../../../routes';
import useActiveUser from '../../../hooks/useActiveUser';
import { getUserImage } from '../../../helpers/user';
import useHasActiveFeatureFlag from '../../../hooks/useFeatureFlags';
import { FF_CHAT } from '../../../components/FeatureFlagWrapper';
import { chatCurrentUserModel } from '../../../models/chat';
import { getChatSession } from '../../../helpers/chatSession';
import { withChatImpersonationGuard } from '../Components/ChatImpersonationGuard';

const conversationListWidth = 400;

const SDChatDesktop = () => {
  const appId = process.env.REACT_APP_TALKJS_APP_ID;
  const history = useHistory();
  const user = useActiveUser();
  const impersonatedUser = useSelector((state) => state.app.impersonating);
  const idTokenPayload = useSelector((state) => state.app.idTokenPayload);
  const hasActiveChatFF = useHasActiveFeatureFlag(FF_CHAT);
  const picture = getUserImage(user, !!impersonatedUser ? null : idTokenPayload, true); // user, token, skip fallback
  const hasAccess =
    currentlyLoggedInOrImpersonatingUserHasAnyRoleInCurrentlySelectedSite([
      Roles.Admin,
      Roles.NationalSiteDirector,
      Roles.SiteDirector,
      Roles.SiteCoordinator,
    ]) && hasActiveChatFF;

  const userId = String(user?.id);
  const session = getChatSession(userId);
  const [activeTab, setActiveTab] = useState('open');
  const [currentConversation, setCurrentConversation] = useState(null);

  const conversationFilter = {
    hasMessages: true,
    custom: { resolved: ['==', activeTab === 'resolved' ? 'true' : 'false'] },
  };

  if (!hasAccess) {
    return history.push(routes.index);
  }

  useEffect(() => {
    session.currentUser.set(chatCurrentUserModel({ user, picture, isSiteStaff: true }));
  }, []);

  const handleChatboxClose = () => {
    setCurrentConversation(null);
  };

  const conversationListTheme = useMemo(() => ({
    ConversationListItem: CustomConversationListItem,
  }));

  const chatTheme = useMemo(() => ({
    ChatHeader: (props) => <SDCustomChatHeader {...props} session={session} onChatClose={handleChatboxClose} />,
    Message: (props) => <CustomMessage {...props} isSiteStaff />,
  }));

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 160px)', boxShadow: 3, border: 1, borderColor: 'divider' }}>
      <Box sx={{ width: conversationListWidth, display: 'flex', flexDirection: 'column' }}>
        <SDCustomListHeader
          session={session}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          conversationListWidth={conversationListWidth}
        />
        <ConversationList
          style={{ width: conversationListWidth, borderRadius: 0, borderBottom: 0, borderLeft: 0, height: '100%' }}
          appId={appId}
          userId={userId}
          conversationFilter={conversationFilter}
          onSelectConversation={(event) => {
            setCurrentConversation(event.conversation.id);
          }}
          selectedConversationId={currentConversation}
          theme={conversationListTheme}
        />
      </Box>
      {currentConversation && (
        <Chatbox
          style={{ width: '100%', height: '100%', borderRadius: 0, border: 0 }}
          appId={appId}
          userId={userId}
          conversationId={currentConversation}
          theme={chatTheme}
        />
      )}
    </Box>
  );
};

export default withChatImpersonationGuard(SDChatDesktop);
